import { Card } from '@lms/ui/card';
import { cn } from '@lms/ui/utils';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import type * as React from 'react';
import { formatNumber, formatPercent } from '../format';

/**
 * A headline number. With `of`, it reads as "12 / 30" with the share and a
 * meter underneath, so progress towards the whole is visible at a glance.
 * With `delta`, the change against an earlier value is shown underneath.
 */
export function KpiTile({
  label,
  description,
  value,
  of,
  meter = true,
  delta,
  locale,
  className,
}: {
  label: React.ReactNode;
  description?: React.ReactNode;
  value: number;
  of?: number;
  /** Show the share and meter for `of` (default true). */
  meter?: boolean;
  /** Compare against an earlier value, e.g. "vs last year". */
  delta?: { previous: number; label: string };
  locale?: string;
  className?: string;
}) {
  const ratio =
    of !== undefined && of > 0 ? Math.min(Math.max(value / of, 0), 1) : 0;
  const showMeter = of !== undefined && meter;

  return (
    <Card className={cn('flex min-w-0 flex-col gap-4 p-5', className)}>
      {/* Fixed header height, so values line up across tiles in a row
          whether or not they have a description. */}
      <div className="min-h-8 space-y-1">
        <h3 className="text-sm leading-none font-medium">{label}</h3>
        {description && (
          <p className="text-muted-foreground text-xs">{description}</p>
        )}
      </div>
      <div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-4xl leading-none font-semibold tracking-tight">
            {formatNumber(value, locale)}
          </span>
          {of !== undefined && (
            <span className="text-muted-foreground text-lg leading-none font-medium">
              / {formatNumber(of, locale)}
            </span>
          )}
          {showMeter && (
            <span className="text-muted-foreground ml-auto text-sm font-medium">
              {formatPercent(ratio, locale)}
            </span>
          )}
        </div>
        {showMeter && (
          <div
            aria-hidden
            className="bg-primary/15 mt-3 h-1.5 overflow-hidden rounded-full"
          >
            <div
              className="bg-primary h-full rounded-full"
              style={{ width: `${ratio * 100}%` }}
            />
          </div>
        )}
        {delta && <Delta value={value} {...delta} locale={locale} />}
      </div>
    </Card>
  );
}

/**
 * "↗ +18 % vs last year". Neutral ink: the arrow and sign carry the
 * direction, colour doesn't judge it. Hidden when there's nothing to compare
 * against (a previous value of zero has no meaningful percentage).
 */
function Delta({
  value,
  previous,
  label,
  locale,
}: {
  value: number;
  previous: number;
  label: string;
  locale?: string;
}) {
  if (previous <= 0) return null;
  const change = (value - previous) / previous;
  const Icon =
    Math.abs(change) < 0.005
      ? ArrowRight
      : change > 0
        ? ArrowUpRight
        : ArrowDownRight;
  return (
    <p className="text-muted-foreground mt-3 flex items-center gap-1 text-xs">
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span>
        <span className="text-foreground font-medium">
          {change > 0 ? '+' : ''}
          {formatPercent(change, locale)}
        </span>{' '}
        {label}
      </span>
    </p>
  );
}
